/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { DEFAULT_DETECTION_DATE_RANGE } from 'components/exceptions/constants'
import { DetectionDateRange, DetectionKind, DetectionSource, DetectionStatus, DetectionType, SortOrder } from 'components/exceptions/types'

// The dashboard keeps its own single-select fields; the date range is shared on purpose.
export interface ExceptionsFiltersState {
  sources: DetectionSource[]
  detectionTypes: DetectionType[]
  status: DetectionStatus | 'all'
  haulerId: string | 'all'
  sortOrder: SortOrder
  searchTerm: string
  expandedDetectionId: string | null
  confirmationModeFor: string | null
  dashboardSource: DetectionSource | 'all'
  dashboardKind: DetectionKind | 'all'
  dateRange: DetectionDateRange
}

const initialState: ExceptionsFiltersState = {
  sources: [],
  detectionTypes: [],
  // VIS-001 AC2: Status defaults to Pending so reviewers land on the
  // actionable subset of the queue.
  status: 'pending',
  haulerId: 'all',
  sortOrder: 'newest',
  searchTerm: '',
  expandedDetectionId: null,
  confirmationModeFor: null,
  dashboardSource: 'all',
  dashboardKind: 'all',
  dateRange: DEFAULT_DETECTION_DATE_RANGE,
}

// Reset on rehydrate: a persisted open row would point at a detection that may be gone.
export const getTransientExceptionsFilterState = (): Pick<ExceptionsFiltersState, 'expandedDetectionId' | 'confirmationModeFor'> => ({
  expandedDetectionId: null,
  confirmationModeFor: null,
})

const exceptionsFiltersSlice = createSlice({
  name: 'exceptionsFilters',
  initialState,
  reducers: {
    setSources(state, action: PayloadAction<DetectionSource[]>) {
      state.sources = action.payload
    },
    setDetectionTypes(state, action: PayloadAction<DetectionType[]>) {
      state.detectionTypes = action.payload
    },
    setDateRange(state, action: PayloadAction<DetectionDateRange>) {
      state.dateRange = action.payload
    },
    setDashboardSource(state, action: PayloadAction<DetectionSource | 'all'>) {
      state.dashboardSource = action.payload
    },
    setDashboardKind(state, action: PayloadAction<DetectionKind | 'all'>) {
      state.dashboardKind = action.payload
    },
    setStatus(state, action: PayloadAction<DetectionStatus | 'all'>) {
      state.status = action.payload
    },
    setHaulerId(state, action: PayloadAction<string | 'all'>) {
      state.haulerId = action.payload
    },
    setSortOrder(state, action: PayloadAction<SortOrder>) {
      state.sortOrder = action.payload
    },
    setSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload
    },
    setExpandedDetectionId(state, action: PayloadAction<string | null>) {
      state.expandedDetectionId = action.payload
    },
    openConfirmation(state, action: PayloadAction<string>) {
      state.confirmationModeFor = action.payload
    },
    closeConfirmation(state) {
      state.confirmationModeFor = null
    },
    resetFilters() {
      return initialState
    },
  },
})

export const {
  setSources,
  setDetectionTypes,
  setDateRange,
  setDashboardSource,
  setDashboardKind,
  setStatus,
  setHaulerId,
  setSortOrder,
  setSearchTerm,
  setExpandedDetectionId,
  openConfirmation,
  closeConfirmation,
  resetFilters,
} = exceptionsFiltersSlice.actions

interface StateWithExceptionsFilters {
  exceptionsFilters?: ExceptionsFiltersState
}

const selectExceptionsState = (state: StateWithExceptionsFilters): ExceptionsFiltersState => state.exceptionsFilters ?? initialState

export const selectExceptionsSources = (state: StateWithExceptionsFilters): DetectionSource[] => selectExceptionsState(state).sources
export const selectExceptionsDetectionTypes = (state: StateWithExceptionsFilters): DetectionType[] =>
  selectExceptionsState(state).detectionTypes
export const selectExceptionsDateRange = (state: StateWithExceptionsFilters): DetectionDateRange => selectExceptionsState(state).dateRange
export const selectExceptionsDashboardSource = (state: StateWithExceptionsFilters): DetectionSource | 'all' =>
  selectExceptionsState(state).dashboardSource
export const selectExceptionsDashboardKind = (state: StateWithExceptionsFilters): DetectionKind | 'all' =>
  selectExceptionsState(state).dashboardKind
export const selectExceptionsStatus = (state: StateWithExceptionsFilters): DetectionStatus | 'all' => selectExceptionsState(state).status
export const selectExceptionsHaulerId = (state: StateWithExceptionsFilters): string | 'all' => selectExceptionsState(state).haulerId
export const selectExceptionsSortOrder = (state: StateWithExceptionsFilters): SortOrder => selectExceptionsState(state).sortOrder
export const selectExceptionsSearchTerm = (state: StateWithExceptionsFilters): string => selectExceptionsState(state).searchTerm
export const selectExpandedDetectionId = (state: StateWithExceptionsFilters): string | null =>
  selectExceptionsState(state).expandedDetectionId
export const selectConfirmationModeFor = (state: StateWithExceptionsFilters): string | null =>
  selectExceptionsState(state).confirmationModeFor

export default exceptionsFiltersSlice.reducer
